import {
  AlertTriangle,
  ArrowRight,
  Building2,
  KeyRound,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { useAuthWorkspace } from "./useAuthWorkspace";
import { platformBrand } from "../../shared/branding";
import "./technical-mobile-auth.css";

type AuthScreenProps = {
  mode?: "sign-in" | "service-unavailable";
  onRetry?: () => void;
};

type EntryMethod = "corporate" | "technical" | "shift-form";

export function AuthScreen({ mode = "sign-in", onRetry }: AuthScreenProps) {
  const { search, error, serviceUnavailable, corporateReturnTo, personnelCode, setPersonnelCode, personnelError, personnelBusy, formPin, setFormPin, formPinError, formPinBusy, personnelLogin, formOperatorLogin, clearPersonnelLogin, clearFormPinLogin } = useAuthWorkspace({ mode });
  const [entryMethod, setEntryMethod] = useState<EntryMethod>(
    search.get("technical") === "1"
      ? "technical"
      : window.location.pathname === "/vardiya-uretim-formu"
        ? "shift-form"
        : "corporate",
  );
  const entryBusy = personnelBusy || formPinBusy;
  const selectEntryMethod = (method: EntryMethod) => {
    if (method === entryMethod) return;
    clearPersonnelLogin();
    clearFormPinLogin();
    setEntryMethod(method);
  };
  const entryDescription =
    entryMethod === "technical"
      ? "Teknik çağrıları telefonunuzdan takip etmek için personel kodunuzu girin."
      : entryMethod === "shift-form"
        ? "Vardiya üretim formuna erişmek için kişisel PIN’inizi girin."
        : "Kurumsal Microsoft 365 hesabınızla devam edin.";

  return (
    <main className="auth-shell">
      <section className="auth-intro" aria-label={platformBrand.name}>
        <div className="auth-intro-brand">
          <img src="/anl-logo-new-2026.webp" alt="ANL" />
          <div>
            <strong>{platformBrand.name}</strong>
            <span>{platformBrand.description}</span>
          </div>
        </div>
        <div className="auth-intro-copy">
          <span>KURUMSAL ÇALIŞMA ALANI</span>
          <h1>Kurumsal süreçler, tek platformda.</h1>
        </div>
        <small>ANL · Kurumsal kullanım</small>
      </section>
      <section className="auth-form-panel">
        <div className="auth-card">
          <span
            className={`auth-status-icon ${serviceUnavailable ? "is-warning" : ""}`}
            aria-hidden="true"
          >
            {serviceUnavailable ? (
              <AlertTriangle size={22} />
            ) : (
              <ShieldCheck size={22} />
            )}
          </span>
          <h2>
            {serviceUnavailable
              ? "Oturum doğrulanamadı"
              : (entryMethod === "corporate" ? error?.title : undefined) ?? `${platformBrand.name}’a giriş yapın`}
          </h2>
          <p>
            {serviceUnavailable
              ? "Kimlik doğrulama servisine ulaşılamıyor. Lütfen yeniden deneyin."
              : (entryMethod === "corporate" ? error?.description : undefined) ?? entryDescription}
          </p>

          {serviceUnavailable ? (
            <button className="auth-submit" type="button" onClick={onRetry}>
              <RefreshCw size={17} />
              Tekrar dene
            </button>
          ) : (
            <div className="auth-entry-options">
              <a href="/etiket">Etiket işlerine PIN ile giriş</a>
              <fieldset className="auth-method-picker">
                <legend>Giriş yöntemi</legend>
                <button
                  type="button"
                  className={entryMethod === "corporate" ? "is-selected" : undefined}
                  aria-pressed={entryMethod === "corporate"}
                  onClick={() => selectEntryMethod("corporate")}
                  disabled={entryBusy}
                >
                  <Building2 size={17} aria-hidden="true" />
                  Kurumsal
                </button>
                <button
                  type="button"
                  className={entryMethod === "technical" ? "is-selected" : undefined}
                  aria-pressed={entryMethod === "technical"}
                  onClick={() => selectEntryMethod("technical")}
                  disabled={entryBusy}
                >
                  <KeyRound size={17} aria-hidden="true" />
                  Teknik ekip
                </button>
                <button
                  type="button"
                  className={entryMethod === "shift-form" ? "is-selected" : undefined}
                  aria-pressed={entryMethod === "shift-form"}
                  onClick={() => selectEntryMethod("shift-form")}
                  disabled={entryBusy}
                >
                  <KeyRound size={17} aria-hidden="true" />
                  Vardiya formu
                </button>
              </fieldset>

              {entryMethod === "corporate" ? (
                <a
                  className="auth-submit"
                  href={`/auth/login?returnTo=${encodeURIComponent(corporateReturnTo)}`}
                >
                  {error
                    ? "Microsoft 365 ile yeniden dene"
                    : "Microsoft 365 ile giriş yap"}
                  <ArrowRight size={17} />
                </a>
              ) : null}

              {entryMethod === "technical" ? (
                <form className="technical-personnel-login" onSubmit={personnelLogin}>
                  <div>
                    <KeyRound size={20} aria-hidden="true" />
                    <span>
                      <strong>Personel koduyla giriş</strong>
                      <small>Telefonunuza teknik çağrı almak için</small>
                    </span>
                  </div>
                  <label htmlFor="technical-personnel-code">6 haneli personel kodu</label>
                  <input
                    id="technical-personnel-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    autoFocus={search.get("technical") === "1"}
                    value={personnelCode}
                    onChange={(event) =>
                      setPersonnelCode(event.target.value.replace(/\D/gu, "").slice(0, 6))
                    }
                    placeholder="••••••"
                    aria-describedby={personnelError ? "technical-personnel-error" : undefined}
                  />
                  {personnelError ? <p id="technical-personnel-error" className="technical-personnel-error" role="alert">{personnelError}</p> : null}
                  <button type="submit" disabled={personnelCode.length !== 6 || personnelBusy}>
                    {personnelBusy ? "Doğrulanıyor…" : "Kod ile giriş yap"}
                    <ArrowRight size={17} aria-hidden="true" />
                  </button>
                </form>
              ) : null}

              {entryMethod === "shift-form" ? (
                <form className="technical-personnel-login" onSubmit={formOperatorLogin}>
                  <div>
                    <KeyRound size={20} aria-hidden="true" />
                    <span>
                      <strong>PIN ile giriş</strong>
                      <small>Yalnız vardiya üretim formuna erişim için</small>
                    </span>
                  </div>
                  <label htmlFor="shift-form-pin">4 haneli kişisel PIN</label>
                  <input
                    id="shift-form-pin"
                    type="password"
                    inputMode="numeric"
                    autoComplete="current-password"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    value={formPin}
                    onChange={(event) =>
                      setFormPin(event.target.value.replace(/\D/gu, "").slice(0, 4))
                    }
                    placeholder="••••"
                    aria-describedby={formPinError ? "shift-form-pin-error" : undefined}
                  />
                  {formPinError ? <p id="shift-form-pin-error" className="technical-personnel-error" role="alert">{formPinError}</p> : null}
                  <button type="submit" disabled={formPin.length !== 4 || formPinBusy}>
                    {formPinBusy ? "Doğrulanıyor…" : "PIN ile giriş yap"}
                    <ArrowRight size={17} aria-hidden="true" />
                  </button>
                </form>
              ) : null}
            </div>
          )}
          <p className="auth-assistance">
            {serviceUnavailable
              ? "Sorun sürerse Bilgi Teknolojileri birimiyle iletişime geçin."
              : "Yalnızca yetkilendirilmiş ANL kullanıcıları."}
          </p>
        </div>
      </section>
    </main>
  );
}
